import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import useDebounce from '../../../components/Shared/Delay/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import { ROUTES } from '../../../constants/routes';
import { buildBreadcrumbs, createBreadcrumbNavigateHandler } from '../../../config/navigation/breadcrumbs';
import {
  Button,
  ListPageHeader,
  TableLayout,
} from '../../../components/ui';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';
import ProfileCloneDialog from '../components/ProfileCloneDialog.jsx';
import ProfileDeleteDialog from '../components/ProfileDeleteDialog.jsx';
import ProfileFormDrawer from '../components/ProfileFormDrawer.jsx';
import ProfileListTable from '../components/ProfileListTable.jsx';
import ProfileTableFilters from '../components/ProfileTableFilters.jsx';
import useProfileList from '../hooks/useProfileList.js';
import useProfileManagementPermissions from '../hooks/useProfileManagementPermissions.js';
import useProfileSx from '../hooks/useProfileSx.js';
import { profileService } from '../services/profileService.js';
import { excludeOwnAssignedProfile } from '../utils/profileAccessUtils.js';
import { parseProfileDeleteError } from '../utils/parseProfileDeleteError.js';
import useUserManagementPermissions from '../../user-management/hooks/useUserManagementPermissions.js';

const PROFILE_MANAGEMENT_ORGANISATION_FILTER_KEY = 'profileManagementOrganisationFilter';

function readStoredOrganisationFilter() {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return null;
    }

    const rawValue = window.sessionStorage.getItem(PROFILE_MANAGEMENT_ORGANISATION_FILTER_KEY);
    if (!rawValue) {
      return null;
    }

    return JSON.parse(rawValue);
  } catch (error) {
    return null;
  }
}

function writeStoredOrganisationFilter(value) {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return;
    }

    if (!value) {
      window.sessionStorage.removeItem(PROFILE_MANAGEMENT_ORGANISATION_FILTER_KEY);
      return;
    }

    window.sessionStorage.setItem(
      PROFILE_MANAGEMENT_ORGANISATION_FILTER_KEY,
      JSON.stringify(value)
    );
  } catch (error) {
    // Ignore storage failures and keep the in-memory UI state working.
  }
}

export default function ProfileManagementPage() {
  return (
    <FeatureThemeProvider>
      <ProfileManagementPageContent />
    </FeatureThemeProvider>
  );
}

function ProfileManagementPageContent() {
  const navigate = useNavigate();
  const { semantic } = useFeatureTheme();
  const profileSx = useProfileSx();
  const { showSnackBar } = useSnackBar();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const {
    canCreate,
    canEdit,
    canDelete,
    canManagePermissions,
    canClone,
  } = useProfileManagementPermissions();
  const { canView: canViewUsers } = useUserManagementPermissions();
  const {
    profiles,
    count,
    isLoading,
    error,
    fetchProfiles,
    prependProfile,
    updateProfileInList,
    removeProfileFromList,
  } = useProfileList();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [organisationFilter, setOrganisationFilter] = useState(() => readStoredOrganisationFilter());
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [cloneTarget, setCloneTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  const manageableProfiles = useMemo(
    () => excludeOwnAssignedProfile(loggedInUser, profiles),
    [loggedInUser, profiles]
  );

  const organisationId =
    loggedInUser?.is_superuser
      ? organisationFilter?.organisation_id || ''
      : loggedInUser?.profile?.organisation?.organisation_id || '';

  useEffect(() => {
    fetchProfiles({
      search: debouncedSearch,
      page,
      limit: pageSize,
      organisationId,
    });
  }, [debouncedSearch, fetchProfiles, organisationId, page, pageSize]);

  const breadcrumbsData = useMemo(
    () => buildBreadcrumbs([
      { label: 'Management', href: ROUTES.MANAGEMENT.PROFILE_MANAGEMENT },
      { label: 'Profile Management' },
    ]),
    []
  );

  const openCreateForm = useCallback(() => {
    setEditingProfile(null);
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((profile) => {
    setEditingProfile(profile);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingProfile(null);
  }, []);

  const handleFormSuccess = useCallback(
    (savedProfile, { isEditMode } = {}) => {
      if (isEditMode) {
        updateProfileInList(savedProfile);
      } else {
        prependProfile(savedProfile);
      }
    },
    [prependProfile, updateProfileInList]
  );

  const handleCreatedRedirect = useCallback(
    (savedProfile) => {
      navigate(`/r/management/profile-management/${savedProfile.id}/permissions`);
    },
    [navigate]
  );

  const handlePermissions = useCallback(
    (profile) => {
      navigate(`/r/management/profile-management/${profile.id}/permissions`);
    },
    [navigate]
  );

  const handleCloneSubmit = useCallback(
    async (payload) => {
      const res = await profileService.cloneProfile(payload);
      if (!res.ok) {
        showSnackBar('Failed to clone profile.', 'error');
        return res;
      }
      if (res.data?.permissions_meta?.trimmed) {
        showSnackBar(
          'Profile cloned. Some permissions were adjusted to your access level.',
          'warning'
        );
      } else {
        showSnackBar('Profile cloned successfully.', 'success');
      }
      await fetchProfiles({
        search: debouncedSearch,
        page,
        limit: pageSize,
        organisationId,
      });
      return res;
    },
    [debouncedSearch, fetchProfiles, organisationId, page, pageSize, showSnackBar]
  );

  const handleViewAssignedUsers = useCallback(
    (profile) => {
      if (!profile?.id) {
        return;
      }
      setDeleteTarget(null);
      navigate(`${ROUTES.MANAGEMENT.USER_MANAGEMENT}?profileId=${profile.id}`);
    },
    [navigate]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) {
      return;
    }
    setIsDeleting(true);
    try {
      const res = await profileService.deleteProfile(deleteTarget.id);
      if (!res.ok) {
        const profileInUse = parseProfileDeleteError(res.data);
        if (profileInUse) {
          showSnackBar(profileInUse.message, 'warning');
          return;
        }
        showSnackBar('Failed to delete profile.', 'error');
        return;
      }
      removeProfileFromList(deleteTarget.id);
      showSnackBar('Profile deleted successfully.', 'success');
      setDeleteTarget(null);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, removeProfileFromList, showSnackBar]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        minHeight: 0,
        px: { xs: 2, md: 3 },
        py: 2.5,
        bgcolor: semantic.surfaces.surfaceSubtle,
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={createBreadcrumbNavigateHandler(navigate)}
      />

      <ListPageHeader
        title="Profile Management"
        subtitle="Manage permission profiles for your organisation."
        action={
          canCreate ? (
            <Button variant="primary" icon={<AddIcon />} onClick={openCreateForm}>
              Add Profile
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <ProfileTableFilters
          search={search}
          // onSearchChange={(value) => {
          //   setSearch(value);
          //   setPage(0);
          // }}
          onSearchChange={(value) => {
            const searchValue = value
              .replace(/^\s+/, '')
              .replace(/\s{2,}/g, ' ');

            setSearch(searchValue);
            setPage(0);
          }}

          organisationFilter={organisationFilter}
          onOrganisationChange={(value) => {
            setOrganisationFilter(value);
            writeStoredOrganisationFilter(value);
            setPage(0);
          }}
          showOrgFilter={Boolean(loggedInUser?.is_superuser)}
        />

        <TableLayout
          variant="flat"
          sx={profileSx.tableLayout}
          pagination={{
            page,
            pageSize,
            totalCount: count,
            onPageChange: setPage,
            onPageSizeChange: (nextPageSize) => {
              setPageSize(nextPageSize);
              setPage(0);
            },
          }}
        >
          <ProfileListTable
            profiles={manageableProfiles}
            loading={isLoading}
            page={page}
            pageSize={pageSize}
            onEdit={openEditForm}
            onPermissions={handlePermissions}
            onClone={setCloneTarget}
            onDelete={setDeleteTarget}
            canEdit={canEdit}
            canManagePermissions={canManagePermissions}
            canClone={canClone}
            canDelete={canDelete}
            showOrganisationColumn={Boolean(loggedInUser?.is_superuser)}
          />
        </TableLayout>
      </Box>

      <ProfileFormDrawer
        open={isFormOpen}
        profile={editingProfile}
        onClose={closeForm}
        onSuccess={handleFormSuccess}
        onCreatedRedirect={handleCreatedRedirect}
      />

      <ProfileCloneDialog
        open={Boolean(cloneTarget)}
        profile={cloneTarget}
        onClose={() => setCloneTarget(null)}
        onSuccess={handleCloneSubmit}
      />

      <ProfileDeleteDialog
        open={Boolean(deleteTarget)}
        profile={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        onViewAssignedUsers={handleViewAssignedUsers}
        canViewUsers={canViewUsers}
        isDeleting={isDeleting}
      />
    </Box>
  );
}
